import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ARTICLES } from "@/lib/articles";
import { api } from "@/lib/api";
import { BlogHeader } from "@/components/BlogHeader";
import { Footer } from "@/components/Footer";

export default function Article() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const staticArticle = ARTICLES.find((a) => a.slug === slug);
  const [fetched, setFetched] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setFetched(null);
    setNotFound(false);
    if (!staticArticle) {
      api.get(`/blog/articles/${slug}`).then((r) => setFetched(r.data)).catch(() => setNotFound(true));
    }
  }, [slug, staticArticle]);

  const article = staticArticle || fetched;

  useEffect(() => {
    if (!article) return;
    const c = article[lang] || article.fr;
    document.title = c.title + " | Smart Telecom Advisor";
    document.querySelector('meta[name="description"]')?.setAttribute("content", c.desc);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: c.title,
      description: c.desc,
      datePublished: article.date,
      inLanguage: lang,
      author: { "@type": "Person", name: "Sam — Smart Telecom Advisor" },
      publisher: { "@type": "Organization", name: "Smart Telecom Advisor", url: "https://www.smartelecomadvisor.be/" },
      mainEntityOfPage: `https://www.smartelecomadvisor.be/blog/${article.slug}`,
    });
    document.getElementById("article-ld")?.remove();
    document.head.appendChild(ld);
    window.scrollTo(0, 0);
    return () => document.getElementById("article-ld")?.remove();
  }, [article, lang]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (!article) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <BlogHeader />
        <main className="max-w-3xl mx-auto px-6 pt-32 pb-20" data-testid="article-loading">
          <p className="text-white/40 text-sm">…</p>
        </main>
      </div>
    );
  }
  const c = article[lang] || article.fr;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <BlogHeader />
      <main className="max-w-3xl mx-auto px-6 pt-32 pb-20" data-testid="article-page">
        <span className="text-[11px] uppercase tracking-widest text-[#FF7900]">{c.tag}</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-3 mb-4">{c.title}</h1>
        <p className="text-xs text-white/35 mb-8">
          {new Date(article.date).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", { day: "numeric", month: "long", year: "numeric" })} · Sam — Smart Telecom Advisor
        </p>
        <p className="text-lg text-white/70 leading-relaxed mb-10">{c.intro}</p>
        {c.sections.map((s, i) => (
          <section key={i} className="mb-9">
            <h2 className="text-lg md:text-xl font-semibold mb-3 text-white/90">{s.h}</h2>
            <p className="text-[15px] text-white/55 leading-relaxed">{s.p}</p>
          </section>
        ))}
        <div className="mt-12 border border-[#FF7900]/40 bg-[#FF7900]/5 p-7" data-testid="article-cta">
          <p className="text-base font-semibold mb-4">{c.cta}</p>
          <Link to="/" data-testid="article-cta-link"
            className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300">
            {t.blog.ctaBtn} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">{t.blog.more}</p>
          <div className="flex flex-col gap-3">
            {ARTICLES.filter((a) => a.slug !== slug).map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="text-sm text-[#FF7900] hover:text-white transition-colors duration-300" data-testid={`article-more-${a.slug}`}>
                → {(a[lang] || a.fr).title}
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
